import moment from "moment";
import "moment/locale/ar";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import date from "../../assets/images/icons/calendar.svg";
import BlueBgImage from "../../assets/images/icons/domcare-background.png";
import editIcon from "../../assets/images/icons/edit_blue.svg";
import filter from "../../assets/images/icons/filter.svg";
import gender from "../../assets/images/icons/gender.svg";
import Icon_doctor_specialty from "../../assets/images/icons/Icon_doctor_certificate.svg";
import Icon_doctor_certificate from "../../assets/images/icons/Icon_doctor_specialty.svg";
import phone from "../../assets/images/icons/phone-dark.svg";
import Icon_price from "../../assets/images/icons/ProfileIcons/bank-note-02.svg";
import Icon_clinic_schedule from "../../assets/images/icons/ProfileIcons/clock-01.svg";
import Icon_clinic_address from "../../assets/images/icons/ProfileIcons/location.svg";
import icon_contact_info from "../../assets/images/icons/ProfileIcons/phone.svg";
import Ic_user from "../../assets/images/icons/user.svg";
import { Drawer, GeneralButton, ProfileAvatar } from "../../components";
import { useGetPersonalInfo } from "../../hooks";
import { RootState } from "../../redux/store";
import { TitleSideLink } from "../../Share";
import { formatBirthday, formatDoctorName } from "../../utils/utils";

const ProfileSidebar: React.FC = () => {
  const { data: userResponse } = useGetPersonalInfo();
  const [isSeeMoreOpen, setIsSeeMoreOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const userData = userResponse?.Data;

  const { t, i18n } = useTranslation();
  moment.locale(i18n.language);
  const { FullName, Image, Bio, FullNameAr, FullNameEn, Id, Birthday, Gender } =
    userData ?? {};
  const UserInfo = useSelector((state: RootState) => state.registration);

  const sections = [
    {
      title: t("doctor_info"),
      links: [
        {
          icon: Icon_doctor_specialty,
          to: "/profile/doctor_specialty",
          label: t("doctor_specialty"),
        },
        {
          icon: Icon_doctor_certificate,
          to: "/profile/doctor_certificate",
          label: t("doctor_certificate"),
        },
      ],
    },
    {
      title: t("clinic_info"),
      links: [
        {
          icon: Icon_clinic_schedule,
          to: "/profile/clinic_schedule",
          label: t("clinic_schedule"),
        },
        {
          icon: Icon_clinic_address,
          to: "/profile/clinic_address",
          label: t("clinic_address"),
        },
        {
          icon: icon_contact_info,
          to: "/profile/contact_info",
          label: t("contact_info"),
        },
        {
          icon: Icon_price,
          to: "/profile/price",
          label: t("session_price"),
        },
      ],
    },
  ];

  const ProfileHeader = () => (
    <div className="flex w-full flex-col items-center gap-4">
      <div className="relative h-[121px]">
        <img src={BlueBgImage} alt="" className="h-[93px] w-96" />
        <div className="absolute -bottom-0 start-5 rounded-full bg-white p-1">
          <ProfileAvatar
            Id={Id}
            FullName={FullName}
            alt="profile"
            size={"medium"}
            src={Image}
            imageToLoad={Ic_user}
            className="h-20 !w-20 rounded-full bg-primary-bg"
            withPreview
          />
        </div>
      </div>
      <div className="flex w-full items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-3 whitespace-nowrap text-lg font-semibold">
            {formatDoctorName(FullName, FullNameAr, FullNameEn)}
            {/* <div
              className={`${CanBook ? "bg-error" : "bg-positive"} h-2.5 w-2.5 rounded-full`}
            /> */}
          </h2>
          <div className="flex flex-col gap-1 text-body-text ">
            {userData?.Specialties?.map((item: any, index: number) => (
              <span key={index} className="text-wrap max-w-64">{item?.Name}</span>
            ))}
          </div>
        </div>
        <Link
          to="/profile"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-2"
        >
          <img src={editIcon} alt="Edit Profile" className="h-4 w-4" />
          <span className="text-nowrap text-sm font-bold text-primary">
            {t("edit_profile")}
          </span>
        </Link>
      </div>
    </div>
  );

  const BioSection = () => {
    if (!Bio || Bio.length === 0) return null;

    return (
      <div className="overflow-hidden break-words text-sm text-body-text">
        {Bio.length < 90 ? (
          <div className="break-words pb-3 text-sm text-body-text">{Bio}</div>
        ) : (
          <div className="break-words pb-3 text-sm text-body-text">
            {!isSeeMoreOpen ? (
              <>
                {Bio.slice(0, 70)}
                <span
                  className="cursor-pointer font-bold text-secondaryDark"
                  onClick={() => setIsSeeMoreOpen(true)}
                >
                  ...{t("more")}
                </span>
              </>
            ) : (
              <>
                {Bio}
                <span
                  className="cursor-pointer font-bold text-secondaryDark"
                  onClick={() => setIsSeeMoreOpen(false)}
                >
                  ...{t("less")}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const ProfileInfoSection = () => {
    const info = [
      {
        icon: date,
        label: t("birth_date"),
        value: formatBirthday(Birthday, i18n.language),
      },
      {
        icon: gender,
        label: t("gender"),
        value: Gender && t(Gender?.toLocaleLowerCase()),
      },
      {
        icon: phone,
        label: t("phone_number"),
        value: (
          <div className="flex items-center gap-1" dir="ltr">
            <span>{UserInfo.userDetails.Country?.Code}</span>
            <span>-</span>
            <span>{UserInfo.userDetails.PhoneNumber}</span>
          </div>
        ),
      },
    ];
    return (
         <div className="flex flex-col gap-1">
          {info.map((e, i) => (
            <div key={i} className="flex items-center justify-between p-2">
              <div className="flex items-center gap-2">
                <img
                  src={e.icon}
                  alt={e.label}
                  className="h-6 w-6 brightness-0"
                />
                <p className="text-body-text">{e.label}</p>
              </div>

              {e.value && <div className="text-sm">{e.value}</div>}
            </div>
          ))}
        </div> 
    );
  };

  const SidebarSections = () => (
    <div className="flex flex-col gap-2 overflow-auto">
      {sections.map((section, index) => (
        <Section key={index} title={section.title}>
          {section.links.map((link, linkIndex) => (
            <TitleSideLink
              key={linkIndex}
              icon={link.icon}
              to={link.to}
              label={link.label}
              className="text-body-text hover:text-darkFont"
            />
          ))}
        </Section>
      ))}
    </div>
  );

  return (
    <div className="relative flex w-full flex-col gap-4 max-lg:mt-4 lg:w-96">
      <div className="ms-auto lg:hidden">
        <GeneralButton
          icon={filter}
          onClick={() => setIsOpen(true)}
          title={t("profile_menu")}
          className="!px-2"
          color="main"
        />
      </div>

      <Drawer
        isOpen={isOpen}
        header={t("profile")}
        customStyle={`w-96 !overflow-auto`}
        onClose={() => setIsOpen(false)}
      >
        <div className="flex flex-col gap-4">
          <ProfileHeader />
          {/* <BioSection /> */}
          <ProfileInfoSection />
        </div>
        <SidebarSections />
      </Drawer>

      <div className="flex h-full w-full min-w-[25.5rem] flex-col pe-2 max-lg:hidden">
        <div className="flex flex-col gap-4">
          <ProfileHeader />
          {/* <BioSection /> */}
          <ProfileInfoSection />
        </div>
        <SidebarSections />
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children?: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="flex flex-col gap-1 border-t-2 border-primary-bg pt-4">
    <h3 className="mb-2 text-sm font-bold text-primary-text">{title}</h3>
    {children}
  </div>
);

export default ProfileSidebar;
